import React, { useState, useMemo } from 'react';
import './RouteEditor.css';

const manhattanDistance = (lat1, lon1, lat2, lon2) => {
  const latDiff = Math.abs(lat2 - lat1) * 111;
  const lngDiff = Math.abs(lon2 - lon1) * 111;
  return latDiff + lngDiff;
};


const ROUTE_COLORS = [
  '#FF6B6B', // red - always start with red
  '#2E7D32', // green
  '#795548', // brown 
  '#9C27B0', // purple
  '#FFD600', // yellow 
  '#FF9800', // orange
  '#00BCD4', // cyan 
  '#E91E63', // pink
  '#3F51B5', // indigo
  '#4CAF50', // light green
  '#FF5722', // deep orange
  '#607D8B'  // blue grey 
];

const RouteEditor = ({ locations, initialRoutes = [], onRouteChange, onRouteVisualizationChange, onConfirmRoutes }) => {
  const [selectedCustomer, setSelectedCustomer] = useState({
    routeId: null,
    customerId: null,
    selectedQty: 0
  });

  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  
  const warehouses = useMemo(() => {
    return locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
  }, [locations]);

  const customers = useMemo(() => {
    return locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
  }, [locations]);

  const [routes, setRoutes] = useState(() => {
    if (initialRoutes.length > 0) {
      return initialRoutes;
    }
    
    const supplyLocations = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
    return supplyLocations.map((warehouse, index) => ({
      id: warehouse.id,
      warehouseId: warehouse.id,
      warehouseName: warehouse.number || `W${index + 1}`,
      color: ROUTE_COLORS[index % ROUTE_COLORS.length],
      customerAssignments: [], // {customerId, assignedQty}
      routePath: [], // not really used much in TP but keeping for compatibility
      isExpanded: true
    }));
  });

  React.useEffect(() => {
    if (initialRoutes.length > 0) {
      setRoutes(initialRoutes);
    }
  }, [initialRoutes]);

  React.useEffect(() => {
    if (warehouses.length === 0) {
      setRoutes([]);
      return;
    }
    
    const warehouseIds = warehouses.map(w => w.id).sort();
    const routeWarehouseIds = routes.map(r => r.warehouseId).sort();
    
    if (JSON.stringify(warehouseIds) !== JSON.stringify(routeWarehouseIds)) {
      const newRoutes = warehouses.map((warehouse, index) => {
        const existingRoute = routes.find(route => route.warehouseId === warehouse.id);
        
        return {
          id: warehouse.id,
          warehouseId: warehouse.id,
          warehouseName: warehouse.number || `W${index + 1}`,
          color: existingRoute?.color || ROUTE_COLORS[index % ROUTE_COLORS.length],
          customerAssignments: existingRoute?.customerAssignments || [],
          routePath: existingRoute?.routePath || [],
          isExpanded: existingRoute?.isExpanded ?? true
        };
      });
      
      setRoutes(newRoutes);
    }
  }, [warehouses, routes]);

  React.useEffect(() => {
    if (onRouteChange) {
      onRouteChange(routes);
    }
  }, [routes]);

  React.useEffect(() => {
    if (onRouteVisualizationChange) {
      const timer = setTimeout(() => {
        const visibleRoutes = routes.filter(route => route.routePath.length > 1);
        onRouteVisualizationChange(visibleRoutes);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [routes]);

  const handleCustomerClick = (routeId, customerId) => {
    const customer = getCustomerById(customerId);
    if (!customer) return;
    
    const customerAssignedQty = routes.reduce((total, route) => {
      const assignment = route.customerAssignments.find(a => a.customerId === customerId);
      return total + (assignment ? assignment.assignedQty : 0);
    }, 0);
    
    const customerAvailableQty = Number(customer.quantity || 0) - customerAssignedQty;
    
    const route = routes.find(r => r.id === routeId);
    if (!route) return; // shouldn't happen
    
    const warehouseRemainingQty = getWarehouseRemainingQty(route.warehouseId, route.customerAssignments);
    const maxAssignableQty = Math.min(customerAvailableQty, warehouseRemainingQty);
    
    if (warehouseRemainingQty <= 0) {
      return; // warehouse is full
    }
    
    setSelectedCustomer({
      routeId,
      customerId,
      selectedQty: maxAssignableQty // Default to maximum assignable quantity
    });
  };

  // Handle quantity assignment
  const handleQuantityAssignment = (useFullQty = false) => {
    const { routeId, customerId, selectedQty } = selectedCustomer;
    
    if (!routeId || !customerId) return;
    
    const customer = getCustomerById(customerId);
    if (!customer) return;
    
    // Calculate customer's available quantity
    const customerAssignedQty = routes.reduce((total, route) => {
      const assignment = route.customerAssignments.find(a => a.customerId === customerId);
      return total + (assignment ? assignment.assignedQty : 0);
    }, 0);
    
    const customerAvailableQty = Number(customer.quantity || 0) - customerAssignedQty;
    
    // Calculate warehouse remaining capacity
    const route = routes.find(r => r.id === routeId);
    if (!route) return;
    
    const warehouseRemainingQty = getWarehouseRemainingQty(route.warehouseId, route.customerAssignments);
    
    // The maximum assignable quantity is limited by both customer availability and warehouse capacity
    const maxAssignableQty = Math.min(customerAvailableQty, warehouseRemainingQty);
    const qtyToAssign = useFullQty ? maxAssignableQty : Math.min(selectedQty, maxAssignableQty);
    
    if (qtyToAssign <= 0 || qtyToAssign > maxAssignableQty) {
      return; // Invalid quantity
    }
    
    setRoutes(prevRoutes => {
      return prevRoutes.map(route => {
        if (route.id === routeId) {
          // Add or update customer assignment
          const existingIndex = route.customerAssignments.findIndex(a => a.customerId === customerId);
          let newAssignments;
          
          if (existingIndex >= 0) {
            // Update existing assignment
            newAssignments = [...route.customerAssignments];
            newAssignments[existingIndex] = { customerId, assignedQty: qtyToAssign };
          } else {
            // Add new assignment
            newAssignments = [...route.customerAssignments, { customerId, assignedQty: qtyToAssign }];
          }
          
          // Update route path to include warehouse + customers
          const customerIds = newAssignments.map(a => a.customerId);
          const newRoutePath = customerIds.length > 0 
            ? [route.warehouseId, ...customerIds]
            : [];
          
          return {
            ...route,
            customerAssignments: newAssignments,
            routePath: newRoutePath
          };
        }
        return route;
      });
    });
    
    // Clear selection
    setSelectedCustomer({
      routeId: null,
      customerId: null,
      selectedQty: 0
    });
  };

  // Remove customer from route
  const removeCustomerFromRoute = (routeId, customerId) => {
    setRoutes(prevRoutes => {
      return prevRoutes.map(route => {
        if (route.id === routeId) {
          const newAssignments = route.customerAssignments.filter(a => a.customerId !== customerId);
          const customerIds = newAssignments.map(a => a.customerId);
          const newRoutePath = customerIds.length > 0 
            ? [route.warehouseId, ...customerIds]
            : [];
          
          return {
            ...route,
            customerAssignments: newAssignments,
            routePath: newRoutePath
          };
        }
        return route;
      });
    });
  };

  // Remove customer from all routes (to reassign to another route)
  const removeCustomerFromAllRoutes = (customerId) => {
    setRoutes(prevRoutes => {
      return prevRoutes.map(route => ({
        ...route,
        customerIds: route.customerIds.filter(id => id !== customerId)
      }));
    });
  };

  // Toggle route expansion
  const toggleRouteExpansion = (routeId) => {
    setRoutes(prevRoutes => {
      return prevRoutes.map(route => ({
        ...route,
        isExpanded: route.id === routeId ? !route.isExpanded : route.isExpanded
      }));
    });
  };


  // Get customer by ID
  const getCustomerById = (customerId) => {
    return customers.find(customer => customer.id === customerId);
  };

  // Get customers with available quantity
  const availableCustomers = useMemo(() => {
    return customers.map(customer => {
      // Calculate total assigned quantity for this customer across all routes
      const totalAssigned = routes.reduce((total, route) => {
        const assignment = route.customerAssignments.find(a => a.customerId === customer.id);
        return total + (assignment ? assignment.assignedQty : 0);
      }, 0);
      
      const availableQty = Number(customer.quantity || 0) - totalAssigned;
      
      return {
        ...customer,
        totalAssigned,
        availableQty,
        hasAvailableQty: availableQty > 0
      };
    }).filter(customer => customer.hasAvailableQty);
  }, [customers, routes]);

  // Calculate total distance for all flows from a warehouse
  const calculateTotalFlowDistance = (customerAssignments, warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    if (!warehouse) return 0;
    
    let totalDistance = 0;
    customerAssignments.forEach(assignment => {
      const customer = customers.find(c => c.id === assignment.customerId);
      if (customer) {
        const flowDistance = manhattanDistance(
          warehouse.latitude, warehouse.longitude,
          customer.latitude, customer.longitude
        );
        totalDistance += flowDistance;
      }
    });
    
    return Math.round(totalDistance * 100) / 100; // round to 2 decimal places
  };

  const totalDistance = useMemo(() => {
    return routes.reduce((total, route) => {
      const flowDistance = calculateTotalFlowDistance(route.customerAssignments, route.warehouseId);
      return total + flowDistance;
    }, 0);
  }, [routes, locations, warehouses, customers]);

  // Calculate allocation status and validation
  const allocationStatus = useMemo(() => {
    // Calculate total supply and demand
    const totalSupply = warehouses.reduce((sum, w) => sum + Number(w.quantity || 0), 0);
    const totalDemand = customers.reduce((sum, c) => sum + Number(c.quantity || 0), 0);
    
    // Calculate total assigned quantities
    const totalAssignedSupply = routes.reduce((sum, route) => {
      const routeAssigned = route.customerAssignments.reduce((routeSum, assignment) => {
        return routeSum + Number(assignment.assignedQty || 0);
      }, 0);
      return sum + routeAssigned;
    }, 0);
    
    const totalAssignedDemand = customers.reduce((sum, customer) => {
      const customerAssigned = routes.reduce((customerSum, route) => {
        const assignment = route.customerAssignments.find(a => a.customerId === customer.id);
        return customerSum + (assignment ? assignment.assignedQty : 0);
      }, 0);
      return sum + customerAssigned;
    }, 0);
    
    // Determine the scenario and validation rules
    const isBalanced = totalSupply === totalDemand;
    const supplyExcess = totalSupply > totalDemand;
    const supplyShortage = totalSupply < totalDemand;
    
    let canConfirm = false;
    let validationMessage = '';
    
    if (isBalanced) {
      // Balanced: Both supply and demand must be fully used
      const supplyFullyUsed = totalAssignedSupply === totalSupply;
      const demandFullyMet = totalAssignedDemand === totalDemand;
      
      canConfirm = supplyFullyUsed && demandFullyMet;
      
      if (!canConfirm) {
        if (!supplyFullyUsed && !demandFullyMet) {
          validationMessage = `Supply and demand must be fully allocated. Remaining: ${totalSupply - totalAssignedSupply} supply, ${totalDemand - totalAssignedDemand} demand.`;
        } else if (!supplyFullyUsed) {
          validationMessage = `All supply must be allocated. Remaining supply: ${totalSupply - totalAssignedSupply}.`;
        } else {
          validationMessage = `All demand must be satisfied. Remaining demand: ${totalDemand - totalAssignedDemand}.`;
        }
      }
    } else if (supplyExcess) {
      // Supply > Demand: All demand must be satisfied
      const demandFullyMet = totalAssignedDemand === totalDemand;
      
      canConfirm = demandFullyMet;
      
      if (!canConfirm) {
        validationMessage = `All customer demand must be satisfied. Remaining demand: ${totalDemand - totalAssignedDemand}.`;
      }
    } else if (supplyShortage) {
      // Supply < Demand: All supply must be used
      const supplyFullyUsed = totalAssignedSupply === totalSupply;
      
      canConfirm = supplyFullyUsed;
      
      if (!canConfirm) {
        validationMessage = `All warehouse supply must be allocated. Remaining supply: ${totalSupply - totalAssignedSupply}.`;
      }
    }
    
    return {
      totalSupply,
      totalDemand,
      totalAssignedSupply,
      totalAssignedDemand,
      isBalanced,
      supplyExcess,
      supplyShortage,
      canConfirm,
      validationMessage
    };
  }, [routes, warehouses, customers]);

  // Confirm and store custom routes
  const handleConfirmRoutes = async () => {
    setIsConfirming(true);
    setConfirmMessage('');

    try {
      // Prepare route data for storage
      const routeData = {
        routes: routes.map(route => ({
          warehouseId: route.warehouseId,
          warehouseName: route.warehouseName,
          color: route.color,
          customerAssignments: route.customerAssignments,
          routePath: route.routePath,
          distance: calculateRouteDistance(route.routePath)
        })).filter(route => route.routePath.length > 1),
        totalDistance: Math.round(totalDistance * 100) / 100,
        timestamp: new Date().toISOString(),
        locations: locations.map(loc => ({
          id: loc.id,
          number: loc.number,
          latitude: loc.latitude,
          longitude: loc.longitude,
          tpType: loc.tpType,
          quantity: loc.quantity
        }))
      };

      // Store in sessionStorage for comparison mode
      sessionStorage.setItem('tp_custom_routes', JSON.stringify(routeData));
      
      // Store in localStorage for persistence
      const existingData = JSON.parse(localStorage.getItem('tp_saved_routes') || '[]');
      const newData = {
        id: Date.now(),
        name: `Custom Routes ${new Date().toLocaleString()}`,
        data: routeData,
        createdAt: new Date().toISOString()
      };
      
      existingData.push(newData);
      localStorage.setItem('tp_saved_routes', JSON.stringify(existingData));

      setConfirmMessage('Routes confirmed and saved for comparison!');
      
      // Notify parent component about confirmation
      if (onConfirmRoutes) {
        onConfirmRoutes(routeData);
      }
      
      // Auto-hide message after 3 seconds
      setTimeout(() => {
        setConfirmMessage('');
      }, 3000);

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[TP] Error saving routes:', error);
      }
      setConfirmMessage('Error saving routes. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  // Reset all routes
  const handleResetRoutes = () => {
    setRoutes(prevRoutes => {
      return prevRoutes.map(route => ({
        ...route,
        customerAssignments: [],
        routePath: []
      }));
    });
    
    // Clear any selected customer
    setSelectedCustomer({
      routeId: null,
      customerId: null,
      selectedQty: 0
    });
    
    setConfirmMessage('All routes have been reset.');
    
    // Auto-hide message after 2 seconds
    setTimeout(() => {
      setConfirmMessage('');
    }, 2000);
  };

  const getWarehouseRemainingQty = (warehouseId, customerAssignments) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    const warehouseQty = Number(warehouse?.quantity || 0);
    
    const totalAssignedQty = customerAssignments.reduce((total, assignment) => {
      return total + Number(assignment.assignedQty || 0);
    }, 0);
    
    return Math.max(0, warehouseQty - totalAssignedQty); // can't be negative
  };

  // Legacy function - not really needed for TP but some old code might use it
  const calculateRouteDistance = (routePath) => {
    if (!routePath || routePath.length < 2) {
      return 0;
    }
    
    let totalDistance = 0;
    
    for (let i = 0; i < routePath.length - 1; i++) {
      const currentLocation = locations.find(loc => loc.id === routePath[i]);
      const nextLocation = locations.find(loc => loc.id === routePath[i + 1]);
      
      if (currentLocation && nextLocation) {
        const segmentDistance = manhattanDistance(
          currentLocation.latitude, currentLocation.longitude,
          nextLocation.latitude, nextLocation.longitude
        );
        totalDistance += segmentDistance;
      } else {
      }
    }
    
    const result = Math.round(totalDistance * 100) / 100;
    return result;
  };

  if (warehouses.length === 0) {
    return (
      <div className="route-editor-container">
        <h4 className="route-editor-title">Route Editor</h4>
        <div className="empty-state">
          <p>No warehouses (supply points) found. Please add supply locations to create routes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="route-editor-container">
      <h4 className="route-editor-title">Route Editor</h4>
      <p className="route-editor-description">
        Assign customers to warehouse routes by point numbers. Each warehouse gets its own colored route.
      </p>

      {/* Routes */}
      <div className="routes-list">
        {routes.map((route) => (
          <div key={route.id} className="route-item">
            <div 
              className="route-header"
              style={{ 
                backgroundColor: route.color.startsWith('#') 
                  ? `${route.color}E6` // Add 90% opacity (0.9 * 255 = 229.5 ≈ 230 = E6 in hex)
                  : route.color.replace('rgb(', 'rgba(').replace(')', ', 0.9)')
              }}
            >
              <div 
                className="route-header-main"
                onClick={() => toggleRouteExpansion(route.id)}
              >
                <div className="route-info">
                <span className="route-name">{route.warehouseName}</span>
                <span className={`remaining-qty ${getWarehouseRemainingQty(route.warehouseId, route.customerAssignments) === 0 ? 'zero' : getWarehouseRemainingQty(route.warehouseId, route.customerAssignments) < 10 ? 'low' : ''}`}>
                  (Remaining: {getWarehouseRemainingQty(route.warehouseId, route.customerAssignments)})
                </span>
                </div>
                <button className="expand-toggle">
                  {route.isExpanded ? '▼' : '▶'}
                </button>
              </div>
            </div>

            {route.isExpanded && (
              <div className="route-content">
                {/* Assigned customers with flow information */}
                {route.customerAssignments.length > 0 ? (
                  <div className="assigned-customers">
                    <div className="assigned-customers-header">
                      <h5>Assigned Customers:</h5>
                      <span className="total-flow-distance" style={{fontSize: '0.9em', color: '#666', marginLeft: '10px'}}>
                        Total Distance: {calculateTotalFlowDistance(route.customerAssignments, route.warehouseId)} km
                      </span>
                    </div>
                    <div className="customer-list">
                      {route.customerAssignments.map(assignment => {
                        const customer = getCustomerById(assignment.customerId);
                        if (!customer) return null;
                        
                        // Calculate flow distance
                        const warehouse = warehouses.find(w => w.id === route.warehouseId);
                        const flowDistance = warehouse && customer ? 
                          manhattanDistance(warehouse.latitude, warehouse.longitude, customer.latitude, customer.longitude) : 0;
                        
                        return (
                          <div key={assignment.customerId} className="customer-item assigned" style={{display: 'flex', alignItems: 'center', width: '100%', padding: '8px'}}>
                            <span className="flow-info" style={{color: '#333', fontWeight: 'bold', fontSize: '0.9em', flex: '1 1 40%'}}>
                              {route.warehouseName} → {customer.number || customer.name}
                            </span>
                            <span className="flow-distance" style={{color: '#666', fontSize: '0.85em', textAlign: 'center', flex: '1 1 20%'}}>
                              {flowDistance.toFixed(2)}km
                            </span>
                            <span className="customer-details" style={{textAlign: 'right', flex: '1 1 40%'}}>
                              Assigned: {assignment.assignedQty} / {customer.quantity || 0}
                            </span>
                            <button
                              className="remove-customer-btn"
                              onClick={() => removeCustomerFromRoute(route.id, assignment.customerId)}
                              title="Remove from route"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="no-customers">
                    <p>No customers assigned to this route.</p>
                  </div>
                )}

                {/* Available customers to add */}
                {availableCustomers.length > 0 && (
                  <div className="available-customers">
                    <h5>Available Customers:</h5>
                    <div className="customer-list">
                      {availableCustomers.map(customer => {
                        const isSelected = selectedCustomer.routeId === route.id && selectedCustomer.customerId === customer.id;
                        
                        // Check if this warehouse can serve this customer
                        const warehouseRemainingQty = getWarehouseRemainingQty(route.warehouseId, route.customerAssignments);
                        const canServeCustomer = warehouseRemainingQty > 0;
                        const maxAssignableToThisRoute = Math.min(customer.availableQty, warehouseRemainingQty);
                        
                        if (!canServeCustomer) {
                          return null; // Don't show customers that can't be served by this warehouse
                        }
                        
                        return (
                          <div key={customer.id}>
                            <div 
                              className={`customer-item available ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleCustomerClick(route.id, customer.id)}
                            >
                              <span className="customer-name">
                                {customer.number || customer.id}
                              </span>
                              <span className="customer-details">
                                Available: {customer.availableQty} / {customer.quantity || 0}
                                {maxAssignableToThisRoute < customer.availableQty && (
                                  <span className="capacity-limit"> (Max: {maxAssignableToThisRoute})</span>
                                )}
                              </span>
                              <span className="expand-indicator">
                                {isSelected ? '▼' : '▶'}
                              </span>
                            </div>
                            
                            {/* Quantity selection panel */}
                            {isSelected && (
                              <div className="quantity-selection-panel">
                                <div className="quantity-options">
                                  <button
                                    className="quantity-btn full-qty"
                                    onClick={() => handleQuantityAssignment(true)}
                                  >
                                    Max Quantity ({maxAssignableToThisRoute})
                                  </button>
                                  
                                  <div className="custom-qty-section">
                                    <label>Custom Quantity (Max: {maxAssignableToThisRoute}):</label>
                                    <div className="qty-input-group">
                                      <input
                                        type="number"
                                        min="1"
                                        max={maxAssignableToThisRoute}
                                        value={selectedCustomer.selectedQty}
                                        onChange={(e) => setSelectedCustomer(prev => ({ 
                                          ...prev, 
                                          selectedQty: Math.min(Number(e.target.value), maxAssignableToThisRoute)
                                        }))}
                                        className="qty-input"
                                      />
                                      <button
                                        className="assign-btn"
                                        onClick={() => handleQuantityAssignment(false)}
                                        disabled={selectedCustomer.selectedQty <= 0 || selectedCustomer.selectedQty > maxAssignableToThisRoute}
                                      >
                                        Assign
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="route-summary">
        <div className="summary-item total-distance">
          <strong>Total Distance:</strong> {Math.round(totalDistance * 100) / 100} km
        </div>
        
        {/* Action buttons */}
        <div className="route-actions-buttons">
          <button
            className="reset-routes-btn"
            onClick={handleResetRoutes}
            disabled={isConfirming || routes.every(r => r.customerAssignments.length === 0)}
          >
            Reset Routes
          </button>
          
          <button
            className="confirm-routes-btn"
            onClick={handleConfirmRoutes}
            disabled={isConfirming || !allocationStatus.canConfirm}
            title={allocationStatus.canConfirm ? 'Confirm routes' : allocationStatus.validationMessage}
          >
            {isConfirming ? 'Confirming...' : 'Confirm Routes'}
          </button>
        </div>
        
        {/* Validation message */}
        {!allocationStatus.canConfirm && allocationStatus.validationMessage && (
          <div className="validation-message">
            {allocationStatus.validationMessage}
          </div>
        )}
        
        {/* Confirmation message */}
        {confirmMessage && (
          <div className={`confirm-message ${confirmMessage.includes('Error') ? 'error' : 'success'}`}>
            {confirmMessage}
          </div>
        )}
      </div>

    </div>
  );
};

export default RouteEditor;
